import { createListenerMiddleware } from '@reduxjs/toolkit'
import { LOCATION_CHANGE } from 'lib/routing/appRouting'
import {
  SET_BYPASS_REGION_CANDIDATE_SETTING_ID,
  SET_BYPASS_REGION_EXCLUSION_OFFER_IDS,
  SET_BYPASS_REGION_SETTING_ID,
  SET_FREE_PREVIEW_AUTO_ENROL,
} from 'actions/actionConstants'
import { getRegionChannelSettings, REGION_CHANNEL_SETTING_TYPE } from 'api/promo'
import { isOfferRegionBypassDeniedByExperiment } from 'lib/experiments/offerRegionBypassExperiment'
import { getCookies, setCookie } from 'lib/web/cookieUtils'
import config from 'constants/config'

// Client-side fallback for SPA navigations where SSR didn't seed the region/channel settings.
// Mirrors fetchRegionChannelSettings on the server: one svc-promo request resolves every setting,
// and each setting keeps its own cookie state.
//
// Offer region bypass (MLOS): the cookie marks that this user's region + UTM *matched* a bypass
// rule (candidate for the offer_region_bypass experiment). Whether bypass is applied is always
// re-derived from the current Optimizely bucket, so the experiment bucket stays the source of
// truth.
const MATCHED_COOKIE_KEY = 'bypass_region_setting_id'
const EXCLUSION_IDS_COOKIE_KEY = 'bypass_region_exclusion_ids'
const BYPASS_COOKIE_TTL_SECONDS = 7 * 24 * 60 * 60 // 7 days

// Free Preview auto-enrolment: eligibility persists until the rule's end_datetime, capped at
// 7 days from the matching visit, so a return visit still enrols on signup.
const FP_COOKIE_KEY = 'fp_auto_enrol'
const FP_MAX_COOKIE_TTL_SECONDS = 7 * 24 * 60 * 60

const fpCookieMaxAgeSeconds = (endDatetime?: string | null): number => {
  if (!endDatetime) return FP_MAX_COOKIE_TTL_SECONDS
  const remaining = Math.floor((new Date(endDatetime).getTime() - Date.now()) / 1000)
  return Math.max(0, Math.min(remaining, FP_MAX_COOKIE_TTL_SECONDS))
}

const regionChannelSettingsMiddleware = createListenerMiddleware()

// Apply candidate (both arms) + active (denied only for explicit control — fail-open) from a
// matched setting id.
function applyMatchedBypassSetting(
  listenerApi: { getState: () => unknown; dispatch: (action: unknown) => unknown },
  matchedSettingId: string,
) {
  listenerApi.dispatch({ type: SET_BYPASS_REGION_CANDIDATE_SETTING_ID, settingId: matchedSettingId })

  if (!isOfferRegionBypassDeniedByExperiment(listenerApi.getState() as App.State)) {
    listenerApi.dispatch({ type: SET_BYPASS_REGION_SETTING_ID, settingId: matchedSettingId })
  }
}

regionChannelSettingsMiddleware.startListening({
  type: LOCATION_CHANGE,
  effect: async(_action, listenerApi) => {
    listenerApi.unsubscribe()

    const state = listenerApi.getState() as App.State
    const cookies = getCookies()

    // 1. Settle each setting from state or its cookie where possible, so we only hit svc-promo
    //    when at least one of them is unresolved.
    let needFreePreview = config.ENABLE_AUTO_ENROLLMENT_FOR_FREE_PREVIEW && !state.marketing.freePreviewAutoEnrol
    if (needFreePreview) {
      const savedFpSettingId = cookies[FP_COOKIE_KEY]
      if (savedFpSettingId) {
        listenerApi.dispatch({ type: SET_FREE_PREVIEW_AUTO_ENROL, enabled: true, settingId: savedFpSettingId })
        needFreePreview = false
      }
    }

    let needBypass = !state.marketing.bypassRegionCandidateSettingId
    if (needBypass) {
      const savedBypassSettingId = cookies[MATCHED_COOKIE_KEY]
      if (savedBypassSettingId) {
        const savedIds = cookies[EXCLUSION_IDS_COOKIE_KEY]
        if (savedIds) {
          listenerApi.dispatch({ type: SET_BYPASS_REGION_EXCLUSION_OFFER_IDS, offerIds: savedIds.split(',') })
        }
        applyMatchedBypassSetting(listenerApi, savedBypassSettingId)
        needBypass = false
      }
    }

    if (!needFreePreview && !needBypass) return

    const { source, medium, campaign, term } = state.utm
    const region = state.geo.currentRegionCode

    try {
      const settings = await getRegionChannelSettings({
        region: region || undefined,
        source: source || undefined,
        medium: medium || undefined,
        campaign: campaign || undefined,
        term: term || undefined,
      })

      if (needFreePreview) {
        const freePreview = settings[REGION_CHANNEL_SETTING_TYPE.freePreviewAutoEnrol]
        listenerApi.dispatch({
          type: SET_FREE_PREVIEW_AUTO_ENROL,
          enabled: freePreview?.enabled ?? false,
          settingId: freePreview?.setting_id ?? null,
        })
        if (freePreview?.enabled) {
          setCookie(
            FP_COOKIE_KEY,
            freePreview.setting_id ?? '1',
            fpCookieMaxAgeSeconds(freePreview.end_datetime),
            '/',
            'Lax',
            document.domain,
          )
        }
      }

      if (needBypass) {
        const bypass = settings[REGION_CHANNEL_SETTING_TYPE.offerRegionFilterBypass]

        // Offer exclusions are global and experiment-independent — always applied when present.
        const excludedOfferIds = bypass?.excluded_offer_ids ?? []
        if (excludedOfferIds.length > 0) {
          setCookie(EXCLUSION_IDS_COOKIE_KEY, excludedOfferIds.join(','), BYPASS_COOKIE_TTL_SECONDS, '/', 'Lax', document.domain)
          listenerApi.dispatch({ type: SET_BYPASS_REGION_EXCLUSION_OFFER_IDS, offerIds: excludedOfferIds })
        }

        // A disabled winning rule means no bypass, same as no rule at all.
        if (bypass?.enabled && bypass.setting_id) {
          setCookie(MATCHED_COOKIE_KEY, bypass.setting_id, BYPASS_COOKIE_TTL_SECONDS, '/', 'Lax', document.domain)
          applyMatchedBypassSetting(listenerApi, bypass.setting_id)
        }
      }
    } catch {
      // non-critical — both settings stay unset
    }
  },
})

export default regionChannelSettingsMiddleware
